import { Box, Chip, chipClasses, Stack, styled } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useIntl } from 'react-intl';
import { useIsScreenLargerThanBreakpoint } from '../../muiTheme';
import type { FilterMode } from './RespondentFilterSettings';
import { getResponseCountFromMetaResults } from './getResponseCountFromMetaResults';
import type { RespondentFilterUsingMetaResultsQuery$data } from './__generated__/RespondentFilterUsingMetaResultsQuery.graphql';

const ChipStack = styled(Stack)(() => ({
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingLeft: 6, // Compensate for overlap, but leave a little it looks better
}));

const chipOverlap = 8;
const StyledChip = styled(Chip)<ChipProps>(({ theme, size }) => ({
    lineHeight: 1.33,
    height: '24px',
    fontSize: size === 'small' ? 12 : 14,
    fontWeight: 'normal',
    border: `solid 1px ${theme.bino.color.white}`, // Must be the background color
    marginLeft: -chipOverlap, // Overlap with the chip on the left
    maxWidth: `calc(100% + ${chipOverlap}px)`, // Having 100% here will sometimes create ellipsis (...)) overflow when not needed
    // paddingLeft: theme.spacing(1.5),
    // paddingRight: theme.spacing(1),
    [`& .${chipClasses.label}`]: {
        paddingTop: 1,
        paddingRight: theme.spacing(0.5),
    },

    // We use the avatar to add the answer count
    flexDirection: 'row-reverse',
    [`& .${chipClasses.avatar}`]: {
        background: 'transparent',
        width: 'auto',
        height: 'auto',
        // paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(1.5),
        marginLeft: 0,
        marginRight: 0,
        whiteSpace: 'nowrap',
        fontSize: 'inherit',
    },
}));
const Label = styled('div')(({ theme }) => ({
    fontSize: '12px',
    color: theme.bino.color.gray,
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));
const Item = styled('div')(({ theme }) => ({
    boxSizing: 'border-box',
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(3), // Some gap
}));

interface SelectedRespondentFiltersProps {
    metaList: ReadonlyArray<{
        readonly metaKey: string;
        readonly metaValues: ReadonlyArray<string>;
        readonly label: string;
        readonly options: ReadonlyArray<{
            readonly label: string;
            readonly value: string;
        }>;
    }>;
    metaResults: RespondentFilterUsingMetaResultsQuery$data['metaResults'];
    filterMode: FilterMode;
    small?: boolean;
}

export default function SelectedRespondentFilters({
    metaList,
    metaResults,
    filterMode,
    small,
}: SelectedRespondentFiltersProps) {
    const { formatMessage } = useIntl();
    const filterModeText =
        filterMode === 'LogicalAnd'
            ? formatMessage({
                  defaultMessage: 'Match all',
                  description: 'Describes a filter that matches all groups',
              })
            : formatMessage({
                  defaultMessage: 'Match any',
                  description: 'Describes a filter that matches any group',
              });

    const filteredMetaList = metaList.filter(
        ({ metaValues }) => metaValues.length,
    );

    const largerThanLg = useIsScreenLargerThanBreakpoint('lg');
    const largerThanMd = useIsScreenLargerThanBreakpoint('md');
    const largerThanSm = useIsScreenLargerThanBreakpoint('sm');
    const maxColumns = Math.min(
        filteredMetaList.length,
        largerThanLg ? 4 : largerThanMd ? 3 : largerThanSm ? 2 : 1,
    );

    if (filteredMetaList.length === 0) {
        return null;
    }

    return (
        <Stack direction="row" flexWrap={largerThanSm ? 'nowrap' : 'wrap'}>
            <Item sx={{ whiteSpace: 'nowrap' }}>
                <Label>Filter</Label>
                <Box fontWeight="bold" fontSize={small ? 12 : 14} mt={0.25}>
                    {filterModeText}
                </Box>
            </Item>
            <Stack
                direction="row"
                flexWrap="wrap"
                position={largerThanSm ? 'relative' : 'static'}
            >
                {filteredMetaList.map(
                    ({ metaKey, label, options, metaValues }) => (
                        <Item
                            key={metaKey}
                            sx={{ maxWidth: 100 / maxColumns + '%' }}
                        >
                            <Label>{label}</Label>
                            <ChipStack>
                                {options
                                    .filter(
                                        ({ value }) =>
                                            metaValues.indexOf(value) > -1,
                                    )
                                    .map((option) => (
                                        <StyledChip
                                            color="primary"
                                            avatar={
                                                <span>
                                                    (
                                                    {getResponseCountFromMetaResults(
                                                        metaKey,
                                                        option.value,
                                                        metaResults,
                                                    )}
                                                    )
                                                </span>
                                            }
                                            label={`${option.label}`}
                                            key={option.value}
                                            size={small ? 'small' : 'medium'}
                                        />
                                    ))}
                            </ChipStack>
                        </Item>
                    ),
                )}
            </Stack>
        </Stack>
    );
}
